import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import React, { useEffect } from 'react';
import useGTMFunction from 'utils/hooks/useGTMFunction';
// Fired on window once gtm.js has finished loading. Consumers that must run
// strictly after GTM (e.g. the Atlas chatbot) wait for these via runAfterGTM.
import { GTM_LOADED_EVENT, GTM_ERROR_EVENT } from 'utils/gtmEvents';

const GTM = ({ fields }) => {
  const code = getFieldValue(fields, 'code');
  const { updateGTMCodeFunction } = useGTMFunction();

  useEffect(() => {
    if (typeof window === 'undefined' || !code) return;

    // --- noscript fallback (body) ---
    const noScript = `<!-- Google Tag Manager (noscript) -->
    <iframe src="https://www.googletagmanager.com/ns.html?id=${code}"
    height="0" width="0" style="display:none;visibility:hidden"></iframe>
    <!-- End Google Tag Manager (noscript) -->`;

    const noScriptTags = document.getElementsByTagName('noscript');
    let isCodeExists = false;
    for (let i = 0; i < noScriptTags.length; i++) {
      const e = noScriptTags[i];
      const isGTM =
        e.innerHTML.search('www.googletagmanager.com/ns.html') !== -1;
      const isCode = e.innerHTML.search('id=' + code) !== -1;
      if (isCodeExists === false && isGTM && isCode) {
        isCodeExists = true;
      }
    }
    if (isCodeExists === false) {
      const noscript = document.createElement('noscript');
      noscript.innerHTML = noScript;
      document.body.insertBefore(noscript, document.body.childNodes[0]);
    }

    // --- gtm.js + gtag config (head) ---
    // Inject as soon as the component mounts. Previously this waited for
    // <main> to have children, which delayed GTM by at least one render and
    // could skip it entirely when page-content had not rendered yet.
    const alreadyInjected =
      document.querySelector(
        `script[src*="googletagmanager.com/gtm.js?id=${code}"]`,
      ) !== null ||
      (window.google_tag_manager && window.google_tag_manager[code]);
    if (alreadyInjected) {
      window.__gtmLoaded = true;
      return;
    }

    const gtmScript = `
  <script>
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=false;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl+'';
      j.onload=function(){w.__gtmLoaded=true;w.dispatchEvent(new CustomEvent('${GTM_LOADED_EVENT}',{detail:{id:i}}));};
      j.onerror=function(){w.__gtmFailed=true;w.dispatchEvent(new CustomEvent('${GTM_ERROR_EVENT}',{detail:{id:i}}));};
      f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${code}');
    </script>
  `;
    const gtagScript = `<script>
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${code}');
    </script>`;

    const parsedGtmScript = document
      .createRange()
      .createContextualFragment(gtmScript);
    document.head.appendChild(parsedGtmScript);

    const parsedGtagScript = document
      .createRange()
      .createContextualFragment(gtagScript);
    document.head.appendChild(parsedGtagScript);
  }, [code]);

  useEffect(() => {
    if (code) {
      updateGTMCodeFunction(code);
    }
  }, [code]);

  return '';
};

export default GTM;
